import React, { useState, useEffect } from 'react';
import { 
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add, Edit, Delete, Circle, LocationOn } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { 
  addPumpToSite, 
  getSiteWithInstruments, 
  updatePumpInSite, 
  deletePumpFromSite,
  getTagNamesByProjectId,
  getTelemetryData,
  logActivity
} from '../../api/apiService';
import { TELEMETRY_LIVE_POLL_MS } from '../../constants/telemetry';

const PumpsTab = ({ projectId, siteData }) => {
  const userRole = useSelector((state) => state.auth.user?.role);
  const currentUser = useSelector((state) => state.auth.user);
  const isAdminOrManager = userRole === 'Admin' || userRole === 'Manager';

  // State for pumps data
  const [pumps, setPumps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [telemetryData, setTelemetryData] = useState({});
  const [isLoadingTelemetry, setIsLoadingTelemetry] = useState(false);

  // State for tag names
  const [tagNames, setTagNames] = useState([]);
  const [loadingTagNames, setLoadingTagNames] = useState(false);

  // State for dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedPump, setSelectedPump] = useState(null);
  const [formData, setFormData] = useState({
    pumpDescription: '',
    pumpLocation: '',
    onOffTagName: '',
    tripTagName: ''
  });

  // Fetch pumps data when component mounts or projectId changes
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        const fullSiteData = await getSiteWithInstruments(projectId);
        setPumps(fullSiteData.pumps || []);
      } catch (error) {
        console.error('Error fetching site data:', error);
        setPumps([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  // Fetch tag names from omsTelemetryLive table
  useEffect(() => {
    const fetchTagNames = async () => {
      if (!projectId) return;
      
      try {
        setLoadingTagNames(true);
        const names = await getTagNamesByProjectId(projectId);
        setTagNames(names);
      } catch (error) {
        console.error('Error fetching tag names:', error);
        setTagNames([]);
      } finally {
        setLoadingTagNames(false);
      }
    };

    fetchTagNames();
  }, [projectId]);

  // Fetch telemetry data for pumps
  useEffect(() => {
    const fetchTelemetryData = async () => {
      if (!projectId || !pumps.length) return;

      try {
        setIsLoadingTelemetry(true);
        const tagIds = [];
        
        // Collect all tag IDs from pumps
        pumps.forEach(pump => {
          if (pump.onOffTagName) {
            // Format tagId from tagName: PRJ1255_PLC1:DO_301
            const serverName = siteData?.serverName || 'PLC1';
            const cleanServerName = serverName.replace(`${projectId}_`, '');
            const formattedTagId = `${projectId}_${cleanServerName}:${pump.onOffTagName}`;
            tagIds.push(formattedTagId);
          }
          if (pump.tripTagName) {
            // Format tagId from tagName: PRJ1255_PLC1:DI_401
            const serverName = siteData?.serverName || 'PLC1';
            const cleanServerName = serverName.replace(`${projectId}_`, '');
            const formattedTagId = `${projectId}_${cleanServerName}:${pump.tripTagName}`;
            tagIds.push(formattedTagId);
          }
        });

        if (tagIds.length === 0) return;

        console.log('[PumpsTab] Fetching telemetry for tagIds:', tagIds);
        const data = await getTelemetryData(projectId, tagIds);
        
        // Create a map of tagId to value
        const telemetryMap = {};
        data.forEach(item => {
          telemetryMap[item.tagId] = {
            value: item.value,
            timestamp: item.timestamp,
            localTime: item.localTime,
            deviceLocalTime: item.deviceLocalTime
          };
        });
        
        setTelemetryData(telemetryMap);
      } catch (error) {
        console.error('[PumpsTab] Error fetching telemetry data:', error);
      } finally {
        setIsLoadingTelemetry(false);
      }
    };

    // Initial fetch
    fetchTelemetryData();

    const intervalId = setInterval(fetchTelemetryData, TELEMETRY_LIVE_POLL_MS);

    return () => clearInterval(intervalId);
  }, [projectId, pumps, siteData]);

  // Handle form data change
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    const newPump = {
      id: Date.now(),
      pumpDescription: formData.pumpDescription,
      pumpLocation: formData.pumpLocation,
      onOffTagName: formData.onOffTagName,
      tripTagName: formData.tripTagName,
    };

    try {
      await addPumpToSite(projectId, newPump);
      console.log('New pump added successfully!');
      
      // Log add pump activity to DynamoDB
      try {
        await logActivity(currentUser.email, `Added pump: ${newPump.pumpDescription}`);
      } catch (err) {
        console.error('Error logging activity to DynamoDB:', err);
      }
      
      const fullSiteData = await getSiteWithInstruments(projectId);
      setPumps(fullSiteData.pumps || []);
      setOpenDialog(false);
      setFormData({
        pumpDescription: '',
        pumpLocation: '',
        onOffTagName: '',
        tripTagName: ''
      });
    } catch (error) {
      console.error('Error adding pump to DynamoDB:', error);
    }
  };

  // Handle Edit
  const handleEdit = (pump) => {
    setSelectedPump(pump);
    setFormData({
      pumpDescription: pump.pumpDescription || '',
      pumpLocation: pump.pumpLocation || '',
      onOffTagName: pump.onOffTagName || '',
      tripTagName: pump.tripTagName || '',
    });
    setOpenEditDialog(true);
  };

  // Handle Delete
  const handleDelete = (pump) => {
    setSelectedPump(pump);
    setOpenDeleteDialog(true);
  };

  // Handle Edit Submit
  const handleEditSubmit = async () => {
    try {
      const updatedPump = {
        ...selectedPump,
        ...formData,
      };
      
      // Ensure we're not creating a new ID
      if (!updatedPump.id) {
        updatedPump.id = selectedPump.id;
      }

      if (userRole === 'Admin' || userRole === 'Manager') {
        await updatePumpInSite(projectId, updatedPump);
        // Log edit pump activity
        try {
          await logActivity(currentUser.email, `Edited pump: ${updatedPump.pumpDescription}`);
        } catch (err) {
          console.error('Error logging edit pump activity to DynamoDB:', err);
        }
      }
      // Update local state for all roles
      const fullSiteData = await getSiteWithInstruments(projectId);
      setPumps(fullSiteData.pumps || []);
      
      // Close dialog and reset state
      setOpenEditDialog(false);
      setSelectedPump(null);
      setFormData({
        pumpDescription: '',
        pumpLocation: '',
        onOffTagName: '',
        tripTagName: ''
      });
    } catch (error) {
      console.error('Error updating pump:', error);
    }
  };

  // Handle Delete Confirm
  const handleDeleteConfirm = async () => {
    try {
      await deletePumpFromSite(projectId, selectedPump.id);
      // Log delete pump activity
      try {
        await logActivity(currentUser.email, `Deleted pump: ${selectedPump.pumpDescription}`);
      } catch (err) {
        console.error('Error logging delete pump activity to DynamoDB:', err);
      }
      console.log('Pump deleted successfully!');
      const fullSiteData = await getSiteWithInstruments(projectId);
      setPumps(fullSiteData.pumps || []);
      setOpenDeleteDialog(false);
      setSelectedPump(null);
    } catch (error) {
      console.error('Error deleting pump:', error);
    }
  };

  // Get pump status based on telemetry data
  const getPumpStatus = (pump) => {
    if (!pump.onOffTagName) {
      return 'Unknown';
    }
    
    // Format tagId from tagName for telemetry lookup
    const serverName = siteData?.serverName || 'PLC1';
    const cleanServerName = serverName.replace(`${projectId}_`, '');
    const onOffTagId = `${projectId}_${cleanServerName}:${pump.onOffTagName}`;
    
    if (!telemetryData[onOffTagId]) {
      return 'Unknown';
    }
    
    const onOffValue = telemetryData[onOffTagId].value;
    
    // Check for trip condition first
    if (pump.tripTagName) {
      const tripTagId = `${projectId}_${cleanServerName}:${pump.tripTagName}`;
      if (telemetryData[tripTagId]) {
        const tripValue = telemetryData[tripTagId].value;
        if (tripValue === '1' || tripValue === 1 || tripValue === 'true' || tripValue === true) {
          return 'Trip';
        }
      }
    }
    
    // Check on/off status
    if (onOffValue === '1' || onOffValue === 1 || onOffValue === 'true' || onOffValue === true) {
      return 'On';
    } else if (onOffValue === '0' || onOffValue === 0 || onOffValue === 'false' || onOffValue === false) {
      return 'Off';
    }
    
    return 'Unknown';
  };

  // Get status indicator component
  const getStatusIndicator = (status) => {
    const isOn = status === 'On';
    const isOff = status === 'Off';
    const isTrip = status === 'Trip';
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* On Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isOn ? theme.palette.status.normalGreen : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isOn ? theme.palette.status.normalGreen : theme.palette.grey[500], 
            fontWeight: isOn ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            ON
          </Typography>
        </Box>
        
        {/* Off Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isOff ? theme.palette.status.alarmRed : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isOff ? theme.palette.status.alarmRed : theme.palette.grey[500], 
            fontWeight: isOff ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            OFF
          </Typography>
        </Box>
        
        {/* Trip Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isTrip ? theme.palette.status.warningAmber : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isTrip ? theme.palette.status.warningAmber : theme.palette.grey[500], 
            fontWeight: isTrip ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            TRIP
          </Typography>
        </Box>
      </Box>
    );
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress sx={{ color: (theme) => theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: { xs: '300px', sm: '350px', md: '400px' },
      overflow: 'auto',
      position: 'relative'
    }}>
      <Stack spacing={2}>
        {pumps.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            color: (theme) => theme.palette.text.secondary
          }}>
            <Typography variant="body1">No pumps found. Add your first pump using the + button.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {pumps.map((pump, index) => {
              const status = getPumpStatus(pump);
              
              return (
                <Card key={pump.id || index} sx={{ 
                  borderRadius: '16px', 
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 12px rgba(0,0,0,0.06)',
                  backgroundColor: (theme) => theme.palette.background.paper,
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 10px 28px rgba(0,0,0,0.45)' : '0 8px 24px rgba(0,0,0,0.10)',
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.1rem',
                        color: (theme) => theme.palette.text.primary,
                        lineHeight: 1.3
                      }}>
                        {pump.pumpDescription}
                      </Typography>
                      {isAdminOrManager && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit Pump">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(pump);
                              }}
                              sx={{
                                color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Pump">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(pump);
                              }}
                              sx={{
                                color: (theme) => theme.palette.error.main,
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                    <Stack spacing={2}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 1.5,
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.10 : 0.04),
                        borderRadius: '8px'
                      }}>
                        <Typography variant="body2" sx={{ 
                          color: (theme) => theme.palette.text.secondary,
                          fontSize: '0.875rem',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <LocationOn sx={{ fontSize: 16, mr: 0.5, color: (theme) => theme.palette.text.secondary }} /> {pump.pumpLocation}
                        </Typography>
                      </Box>
                      
                      {/* Status Indicators */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        p: 2,
                        backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                        borderRadius: '8px'
                      }}>
                        {isLoadingTelemetry ? (
                          <CircularProgress size={20} sx={{ color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main }} />
                        ) : (
                          getStatusIndicator(status)
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Stack>
      
      {isAdminOrManager && (
        <Fab
          color="primary"
          aria-label="add pump"
          onClick={() => setOpenDialog(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            backgroundColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
            color: (theme) => theme.palette.getContrastText(theme.palette.brand?.royalBlue || theme.palette.primary.main),
            '&:hover': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' 
                ? alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.85)
                : theme.palette.brand?.royalBlue || theme.palette.primary.main,
              filter: (theme) => theme.palette.mode === 'dark' ? 'none' : 'brightness(0.95)',
            },
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Add Pump Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: "12px", 
            padding: {
              xs: "12px",
              sm: "16px",
              md: "20px"
            },
            boxShadow: "0px 8px 24px rgba(0,0,0,0.15)"
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: "600", 
          color: "#1A1F36",
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: {
            xs: '1.1rem',
            sm: '1.25rem',
            md: '1.5rem'
          }
        }}>
          Add New Pump
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: "16px" }}>
            <TextField
              fullWidth
              label="Pump Description"
              name="pumpDescription"
              value={formData.pumpDescription}
              onChange={handleFormChange}
              variant="outlined"
              required
            />
            <TextField
              fullWidth
              label="Pump Location"
              name="pumpLocation"
              value={formData.pumpLocation}
              onChange={handleFormChange}
              variant="outlined"
              required
            />
            <FormControl fullWidth>
              <InputLabel id="on-off-tag-label">On/Off Tag Name</InputLabel>
              <Select
                labelId="on-off-tag-label"
                label="On/Off Tag Name"
                name="onOffTagName"
                value={formData.onOffTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
                required
              >
                <MenuItem value="">Select an On/Off Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="trip-tag-label">Trip Tag Name</InputLabel>
              <Select
                labelId="trip-tag-label"
                label="Trip Tag Name"
                name="tripTagName"
                value={formData.tripTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select a Trip Tag Name (Optional)</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ padding: "16px" }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            color="secondary" 
            sx={{ 
              textTransform: "none",
              color: '#666',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary" 
            sx={{ 
              textTransform: "none", 
              boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Pump Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Edit Pump</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: '16px' }}>
            <TextField
              fullWidth
              label="Pump Description"
              name="pumpDescription"
              value={formData.pumpDescription}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Pump Location"
              name="pumpLocation"
              value={formData.pumpLocation}
              onChange={handleFormChange}
              variant="outlined"
            />
            <FormControl fullWidth>
              <InputLabel id="edit-on-off-tag-label">On/Off Tag Name</InputLabel>
              <Select
                labelId="edit-on-off-tag-label"
                label="On/Off Tag Name"
                name="onOffTagName"
                value={formData.onOffTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select an On/Off Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="edit-trip-tag-label">Trip Tag Name</InputLabel>
              <Select
                labelId="edit-trip-tag-label"
                label="Trip Tag Name"
                name="tripTagName"
                value={formData.tripTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select a Trip Tag Name (Optional)</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            color="primary"
          >
            Submit
          </Button>
          <Button 
            onClick={() => setOpenEditDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Pump Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Delete Pump</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this pump?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="primary"
          >
            Delete
          </Button>
          <Button 
            onClick={() => setOpenDeleteDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PumpsTab; 